<template>
  <div>
    <div v-if="!descriptionEmpty" class="popup-info__area section">
      <div class="label">
        {{ $t("labels.description") }}
      </div>
      <formatted-text :value="item.description" />
    </div>

    <div v-if="visibleEffects.length > 0" class="popup-info__area section">
      <div class="label">
        {{ $t("objectNames.effect", 2) }}
      </div>

      <div class="line_effect">
        <template v-for="effect in visibleEffects" :key="effect.id">
          <store-item
            :id="effect.affectedId"
            v-slot="{ item: effectItem }"
            :type="effect.affectedType"
          >
            <button-item>
              {{ effectItem.title }}
            </button-item>
          </store-item>
          <effect-amount
            :effect="effect"
            :rounds="rounds"
            :round-index="round?.index"
            :inline-expression="true"
            :index="0"
            :targets="simulationViewStore.targets"
            :resources="simulationViewStore.resources"
            :indices="simulationViewStore.indices"
          />
          <i class="whitespace-nowrap">
            <template v-if="effect.pendingEffectsVisible && getEffectLength(effect) > 1">
              {{
                $t(
                  "labels.effectInXRounds",
                  {
                    effect: effect.visibility == 'visible' ? getEffectSum(effect) : '',
                    roundName: $t(
                      `roundUnits.${simulationViewStore.simulation?.roundUnit}`,
                      getEffectLength(effect)
                    )
                  },
                  getEffectLength(effect)
                )
              }}
            </template>
          </i>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { formatNumber } from "@/helpers/utility/Formatting";

import FormattedText from "@core/ui/FormattedText.vue";
import Effect from "@editor/items/effect/Effect";
import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import StoreItem from "@editor/items/StoreItem.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import { useEffectStore } from "@editor/items/effect/effects.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import type { BaseEffectItem } from "../effect/EffectItem";
import type { IncidentItem } from "./IncidentItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";

const { t } = useI18n();

const props = defineProps<{
  item: IncidentItem;
  round: SimulationRoundItem;
}>();

const simulationViewStore = useSimulationViewStore();

const scenario = computed(() => simulationViewStore.scenario);
const rounds = computed(() => simulationViewStore.rounds);

const descriptionEmpty = computed(() => {
  const description = props.item.description.trim();
  return (
    description == null || description == "" || description == "<p></p>"
  );
});

const visibleEffects = computed(() => {
  const incident = props.item;
  if (!incident) return [];
  const effects = useEffectStore().getByActivator(incident) ?? [];
  if (scenario.value) {
    if (scenario.value.effectsVisibility == "hidden") return [];
    if (scenario.value.effectsVisibility == "visible") return effects.map((effect): BaseEffectItem => ({ ...effect, visibility: "visible" }));
    if (scenario.value.effectsVisibility == "values-hidden") return effects.map((effect): BaseEffectItem => ({...effect, visibility: "hint" }));
  }
  if (incident?.effectsVisibility == "hidden") return [];
  if (incident?.effectsVisibility == "visible") return effects.map((effect): BaseEffectItem => ({ ...effect, visibility: "visible" }));
  if (incident?.effectsVisibility == "values-hidden") return effects.map((effect): BaseEffectItem => ({...effect, visibility: "hint" }));
  return effects.filter((effect) => effect.visibility !== "hidden");
});

function getEffectLength(effect: BaseEffectItem) {
  return effect?.impulseExpressions?.length ?? 0;
}
function getEffectSum(effect: BaseEffectItem) {
  if (Effect.isSpecial(effect)) {
    return t("labels.effect");
  }

  return formatNumber(
    Effect.sumExpressions(effect.impulseExpressions),
    "displaySign"
  );
}
</script>

<style lang="scss">
.incident-content__lineafter {
  min-width: 6rem;
  width: max-content;
  padding-left: 0.5rem;
  text-align: left;
  display: block;
}
</style>
